<template lang="pug">
  Modal.export-to-csv-modal(
    ref="modalWindow"
    :title="`Export ${title} to CSV`"
    v-bind="$attrs"
  )
    Alert(v-if="errorMessage" type="danger") {{ errorMessage }}
    div(v-else)
      Alert.mb-4(type="info" v-if="debugMode")
        .text-lg: strong Debug mode enabled
        p Closing the modal window will give you a partial CSV file.
      .progress-bar(
        :style="progressStyle"
        :class="progressClasses"
      ): .bar
      Alert(type="success" v-if="completed")
        div.text-lg: strong Export complete, file has been saved to your device.
        div {{ totalRows | number }} rows exported over {{ this.totalPages | number }} chunk(s)
      Alert(v-else-if="totalPages" type="info") Exporting chunk {{ currentPage }} of {{ totalPages }} ({{ progress }}%)...
      Alert(v-else type="info") Starting export...
</template>

<script>
import { csvExport } from "@syntax51/app-kit"
export default {
  props: {
    title: {
      type: String,
      required: true
    },
    endpoint: {
      type: String,
      required: true
    },
    chunkSize: {
      type: Number,
      default: 500
    }
  },
  data: () => ({
    csv: undefined,
    currentPage: 0,
    totalRows: 0,
    totalPages: undefined,
    errorMessage: null,
    destroyed: false,
    debugMode: false,
    exportId: Math.random().toString(36).substring(6, 12)
  }),
  computed: {
    completed() {
      return this.progress === 100
    },
    progress() {
      return this.totalPages ? Math.round((this.currentPage / this.totalPages) * 100) : 0
    },
    progressClasses() {
      return [this.completed && "completed"]
    },
    progressStyle() {
      return {
        "--progress": `${this.progress}%`
      }
    },
    filename() {
      return `${this.title} - ${new Date().toDateString()}.csv`
    }
  },
  methods: {
    isActuallyOpen() {
      return Boolean(this.$refs["modalWindow"])
    },
    exportParams(page) {
      return {
        page,
        count: this.chunkSize,
        csvExport: this.exportId
      }
    },
    onDebugKeyDown(event) {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault()
        this.debugMode = true
      }
    },
    outputCsvToBrowser() {
      if (!this.csv || this.totalRows === 0) {
        return
      }
      this.csv.toBrowser(this.filename)
    },
    parseResponse(response) {
      if (this.destroyed || !this.isActuallyOpen()) {
        throw `Export window closed`
      }

      if (typeof response.data !== "object") {
        throw "Invalid export data received."
      }

      const meta = response.data.meta || {}
      const rows = response.data.items || response.data.data || []

      if (typeof meta.current_page !== "number" || typeof meta.last_page !== "number") {
        throw `Invalid meta data returned starting export.`
      }

      if (meta.last_page <= 0) {
        throw `Unable to determine number of entries to export.`
      }

      if (meta.current_page > meta.last_page) {
        throw `Error starting csv download, non-first page returned.`
      }

      if (meta.per_page !== this.chunkSize) {
        throw `Invalid number of items sent back.`
      }

      // meta.total is number of total items

      return { rows, meta }
    },
    async exportPage(page) {
      const params = this.exportParams(page)

      try {
        const response = await this.$api.get(this.endpoint, { params })

        const { rows, meta } = this.parseResponse(response)

        this.currentPage = page
        this.totalPages = meta.last_page

        this.csv.addRows(rows)
        this.totalRows += rows.length

        if (this.completed) {
          this.outputCsvToBrowser()
        } else {
          this.exportPage(page + 1)
        }
      } catch (errorMessage) {
        this.errorMessage =
          typeof errorMessage === "string" ? errorMessage : "An error has occured exporting the table."
        console.error("Error exporting to CSV:", errorMessage)
      }
    }
  },
  mounted() {
    this.csv = new csvExport()

    this.destroyed = false
    this.errorMessage = undefined
    this.currentPage = 0
    this.totalPages = undefined

    document.addEventListener("keydown", this.onDebugKeyDown)
    this.exportPage(this.currentPage + 1)
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onDebugKeyDown)
    if (this.debugMode && !this.completed) {
      this.outputCsvToBrowser()
    }
    this.$nextTick(() => {
      this.destroyed = true
      this.csv = undefined
    })
  }
}
</script>

<style>
.export-to-csv-modal {
  .modal-body {
    .icon {
      @apply h-10 w-10;
    }

    .progress-bar {
      @apply mb-4 h-10 w-full;
      @apply overflow-hidden;
      @apply rounded bg-gray-200;

      > .bar {
        @apply rounded;
        @apply h-10;
        width: var(--progress);
        @apply bg-primary-default;
        @apply transition-all duration-500;
      }

      &.completed > .bar {
        @apply bg-green-500;
      }
    }
  }
}
</style>
